import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MoreHorizontal, Play, Pause, Square, Pencil, Archive, Monitor, Copy, Filter, FlaskConical, Trash2 } from 'lucide-react';
import DeleteConfirmModal from './DeleteConfirmModal';
import Card from '../UI/Card';
import Badge from '../UI/Badge';
import Button from '../UI/Button';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';
import { strategiesAPI } from '../../services/api';
import { StaggerContainer, AnimatedItem } from '@/components/motion';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../UI/dropdown-menu';

const Sep = () => <span className="text-border/50">|</span>;

const cardBorderClass = {
  running: 'border-green-500/40 shadow-[0_0_12px_-3px_rgba(34,197,94,0.25)]',
  paused: 'border-yellow-500/40 shadow-[0_0_12px_-3px_rgba(234,179,8,0.25)]',
  error: 'border-red-500/40',
};

function MobileStrategyManager({
  strategies,
  filteredStrategies,
  statusFilters,
  setStatusFilters,
  statusCounts,
  actionLoading,
  getParams,
  handleAction,
  handleStopClick,
  handleArchiveClick,
  handleEmergencyStop,
  loadStrategies,
  navigate,
}) {
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filterOptions = [
    { value: 'running', label: `Running (${statusCounts.running})` },
    { value: 'stopped', label: `Stopped (${statusCounts.stopped})` },
    { value: 'paused', label: `Paused (${statusCounts.paused})` },
    { value: 'error', label: `Error (${statusCounts.error})` },
    { value: 'archived', label: `Archived (${statusCounts.archived})` },
  ];

  const handleDuplicate = async (row) => {
    const params = getParams(row);
    try {
      await strategiesAPI.create({
        name: `${row.name} (Copy)`,
        type: row.type,
        provider: row.provider,
        tradingMode: row.trading_mode || 'paper',
        parameters: params,
        brokerConnectionId: row.broker_connection_id || null,
        ...(row.investment_cap != null ? { investmentCap: row.investment_cap } : {}),
      });
      toast.success('Strategy duplicated');
      loadStrategies();
    } catch (err) {
      toast.error(`Failed to duplicate: ${err.message}`);
    }
  };

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center gap-2">
        <MultiSelectDropdown
          label="Filter"
          icon={Filter}
          options={filterOptions}
          selected={statusFilters}
          onChange={setStatusFilters}
        />
        <button
          onClick={handleEmergencyStop}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-red-900/40 border border-red-700 text-red-400 hover:bg-red-900/60 transition-colors"
        >
          ⏹ Stop All
        </button>
      </div>

      {/* Card list */}
      {filteredStrategies.length > 0 ? (
        <StaggerContainer className="flex flex-col gap-4 pb-8">
          {filteredStrategies.map((row) => {
            const isLoading = actionLoading[row.id];
            const isRunning = row.status === 'running';
            const isStopped = row.status === 'stopped';
            const isPaused = row.status === 'paused';
            const isError = row.status === 'error';
            const isArchived = row.status === 'archived';
            const params = getParams(row);
            const statusVariant = { running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger', archived: 'neutral' }[row.status] || 'neutral';
            const borderExtra = cardBorderClass[row.status] || '';

            const skipKeys = new Set(['assetClass', 'symbol', 'sizingMode']);
            const paramEntries = Object.entries(params)
              .filter(([k, v]) => !skipKeys.has(k) && v != null && v !== '' && typeof v !== 'object')
              .map(([k, v]) => {
                const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
                let display = v;
                if (typeof v === 'number') display = Number.isInteger(v) ? v : v.toFixed(2);
                if (typeof v === 'boolean') display = v ? 'Yes' : 'No';
                return [label, String(display)];
              });

            return (
              <AnimatedItem key={row.id}>
                <Card className={`px-3 py-3 ${borderExtra}`}>
                  {/* Row 1: Name + ticker + ellipsis */}
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground truncate">{row.name}</h3>
                        <span className="font-mono text-xs font-semibold text-primary shrink-0">{params.symbol || '—'}</span>
                      </div>
                    </div>

                    {/* Ellipsis menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors shrink-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[180px]">
                        {/* Trading Floor — always */}
                        <DropdownMenuItem onClick={() => navigate(`/strategies/trading-floor/${row.id}`)}>
                          <Monitor className="h-4 w-4 mr-2" />
                          Trading Floor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/analysis/backtesting?strategy=${row.id}`)}>
                          <FlaskConical className="h-4 w-4 mr-2" />
                          Backtest
                        </DropdownMenuItem>

                        {!isArchived && (
                          <>
                            <DropdownMenuSeparator />

                            {/* Start / Resume */}
                            {(isStopped || isPaused || isError) && (
                              <DropdownMenuItem
                                onClick={() => handleAction(row.id, isPaused ? strategiesAPI.resume : strategiesAPI.start, isPaused ? 'resume' : 'start')}
                                disabled={!!isLoading}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                {isPaused ? 'Resume' : 'Start'}
                              </DropdownMenuItem>
                            )}

                            {/* Pause */}
                            {isRunning && (
                              <DropdownMenuItem
                                onClick={() => handleAction(row.id, strategiesAPI.pause, 'pause')}
                                disabled={!!isLoading}
                              >
                                <Pause className="h-4 w-4 mr-2" />
                                Pause
                              </DropdownMenuItem>
                            )}

                            {/* Stop */}
                            {(isRunning || isPaused || isError) && (
                              <DropdownMenuItem
                                onClick={() => handleStopClick(row)}
                                disabled={!!isLoading}
                              >
                                <Square className="h-4 w-4 mr-2" />
                                Stop
                              </DropdownMenuItem>
                            )}

                            {/* Edit */}
                            {(isStopped || isError) && (
                              <DropdownMenuItem onClick={() => navigate(`/strategies/builder/${row.id}`)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Strategy
                              </DropdownMenuItem>
                            )}

                            {/* Duplicate */}
                            <DropdownMenuItem onClick={() => handleDuplicate(row)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>

                            {/* Archive */}
                            {(isStopped || isError) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleArchiveClick(row)}
                                  disabled={!!isLoading}
                                  className="text-red-400 focus:text-red-400"
                                >
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}

                        {isArchived && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleAction(row.id, strategiesAPI.unarchive, 'unarchive')}
                              disabled={!!isLoading}
                              className="text-green-400 focus:text-green-400"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Unarchive
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(row)}
                              disabled={!!isLoading}
                              className="text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Row 2: Badges */}
                  <div className="flex items-center gap-1.5 mt-2">
                    <Badge variant={statusVariant}>{row.status}</Badge>
                    <Badge variant={row.trading_mode === 'paper' ? 'info' : 'warning'}>
                      {row.trading_mode === 'paper' ? 'PAPER' : 'LIVE'}
                    </Badge>
                    <Badge variant={params.assetClass === 'crypto' ? 'warning' : 'info'}>
                      {params.assetClass ? params.assetClass.charAt(0).toUpperCase() + params.assetClass.slice(1) : 'Stocks'}
                    </Badge>
                  </div>

                  {/* Row 3: Compact stats */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 pt-2 border-t border-white/5 text-[11px] text-muted-foreground">
                    <span><span className="text-muted-foreground/60">Type</span> <span className="font-semibold text-foreground capitalize">{row.type?.replace('_', ' ')}</span></span>
                    <Sep />
                    <span><span className="text-muted-foreground/60">Provider</span> <span className="font-semibold text-foreground">{row.provider}</span></span>
                    {row.tick_interval_seconds && (
                      <>
                        <Sep />
                        <span><span className="text-muted-foreground/60">Interval</span> <span className="font-semibold text-foreground">{row.tick_interval_seconds}s</span></span>
                      </>
                    )}
                    {row.last_tick_at && (
                      <>
                        <Sep />
                        <span><span className="text-muted-foreground/60">Last Tick</span> <span className="font-semibold text-foreground">{new Date(row.last_tick_at).toLocaleTimeString()}</span></span>
                      </>
                    )}
                    {row.executor_enabled != null && (
                      <>
                        <Sep />
                        <span><span className="text-muted-foreground/60">Executor</span> <span className={`font-semibold ${row.executor_enabled ? 'text-green-400' : 'text-muted-foreground'}`}>{row.executor_enabled ? 'ON' : 'OFF'}</span></span>
                      </>
                    )}
                    <Sep />
                    <span><span className="text-muted-foreground/60">Created</span> <span className="font-semibold text-foreground">{new Date(row.created_at).toLocaleDateString()}</span></span>
                    {paramEntries.length > 0 && (
                      <>
                        <Sep />
                        {paramEntries.map(([label, value]) => (
                          <span key={label} className="mr-2">
                            <span className="text-muted-foreground/60">{label}</span>{' '}
                            <span className="font-semibold text-foreground">{value}</span>
                          </span>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Error row */}
                  {isError && row.error_message && (
                    <div className="mt-2 px-2 py-1.5 rounded-md bg-red-900/30 border border-red-800/50 text-xs text-red-400">
                      {row.error_message}
                    </div>
                  )}
                </Card>
              </AnimatedItem>
            );
          })}
        </StaggerContainer>
      ) : strategies.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <p className="text-muted-foreground mb-4">No strategies yet</p>
            <Link to="/strategies/builder">
              <Button>Create Your First Strategy</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="text-center py-12">
            <p className="text-muted-foreground">No matching strategies</p>
          </div>
        </Card>
      )}
      <DeleteConfirmModal
        strategy={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await handleAction(deleteTarget.id, strategiesAPI.permanentDelete, 'delete');
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

export default MobileStrategyManager;
